import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { PageHeader, LoadingSpinner } from '../components/UIComponents';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { rfqAPI } from '../utils/api';
import {
  ArrowLeft, Building2, Mail, Phone, MapPin, Clock, User, FileText,
  CheckCircle2, XCircle, AlertTriangle, Eye, ShoppingCart, Upload,
  ChevronDown, ChevronUp, ArrowRightLeft, Send, Shield, Leaf, Zap,
  ExternalLink, Package, History
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  NEW: { label: 'New', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  UNDER_REVIEW: { label: 'Under Review', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  NEEDS_MORE_INFO: { label: 'Needs Info', bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  APPROVED: { label: 'Approved', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  REJECTED: { label: 'Rejected', bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' },
  CONVERTED: { label: 'Converted', bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200' },
};

const StatusBadge = ({ status, large }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.NEW;
  return (
    <span className={`inline-flex items-center gap-1 font-semibold px-2.5 py-1 rounded border ${cfg.bg} ${cfg.text} ${cfg.border} ${large ? 'text-sm' : 'text-[11px]'}`}>
      {cfg.label}
    </span>
  );
};

const FILE_TYPE_LABELS = {
  electricity_bill: 'Electricity Bill',
  dg_log: 'DG Log',
  site_photo: 'Site Photo',
  load_profile: 'Load Profile',
  other: 'Other',
};

const MISSING_ITEMS_OPTIONS = [
  { key: 'electricity_bill', label: 'Electricity Bills (last 6 months)' },
  { key: 'dg_log', label: 'DG Runtime Logs' },
  { key: 'site_photo', label: 'Site Photos' },
  { key: 'load_profile', label: 'Load Profile Data' },
  { key: 'sanctioned_load', label: 'Sanctioned Load Certificate' },
  { key: 'other', label: 'Other Documents' },
];

// ==================== SUB COMPONENTS ====================

const SiteCard = ({ site, index }) => {
  const [expanded, setExpanded] = useState(index === 0);

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`rfq-site-card-${site.rfq_site_id}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">
            S{index + 1}
          </div>
          <div>
            <p className="font-medium text-slate-800 text-sm">{site.site_name}</p>
            <p className="text-[11px] text-slate-500">{[site.city, site.state].filter(Boolean).join(', ')}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {site.has_dg && <span className="text-[10px] font-medium px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded">DG</span>}
          <span className="text-xs text-slate-500">{site.cart_items?.length || 0} items</span>
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </button>

      {expanded && (
        <div className="px-4 py-3 space-y-4">
          {/* Site Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Address</p>
              <p className="text-slate-700 text-xs mt-0.5">{site.address || '-'}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Pincode</p>
              <p className="text-slate-700 text-xs mt-0.5">{site.pincode || '-'}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Site Type</p>
              <p className="text-slate-700 text-xs mt-0.5 capitalize">{site.site_type?.replace('_', ' ') || '-'}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Has Solar</p>
              <p className="text-slate-700 text-xs mt-0.5">{site.has_solar ? `Yes (${site.solar_pv_kwp} kWp)` : 'No'}</p>
            </div>
          </div>

          {/* Cart Items */}
          {site.cart_items?.length > 0 && (
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                <ShoppingCart className="w-3 h-3" /> Cart Items
              </p>
              <div className="space-y-1.5">
                {site.cart_items.map((item, i) => (
                  <div key={item.id || item.sku_code || i} className="flex items-center justify-between bg-slate-50 rounded px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                        item.type === 'buy' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                      }`}>{item.type}</span>
                      <span className="text-sm text-slate-700">{item.product_name}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <span>Qty: {item.quantity}</span>
                      {item.specifications?.power_kw && <span>{item.specifications.power_kw} kW</span>}
                      {item.specifications?.energy_kwh && <span>{item.specifications.energy_kwh} kWh</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Uploads */}
          {site.uploads?.length > 0 && (
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                <Upload className="w-3 h-3" /> Uploads ({site.uploads.length})
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {site.uploads.map(u => (
                  <div key={u.upload_id} className="flex items-center gap-2 bg-slate-50 rounded px-3 py-2">
                    <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-slate-700 truncate">{u.original_filename}</p>
                      <p className="text-[10px] text-slate-400">{FILE_TYPE_LABELS[u.file_type] || u.file_type}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AuditTimeline = ({ audit }) => {
  if (!audit?.length) return <p className="text-xs text-slate-400 italic">No actions recorded yet.</p>;

  const ACTION_LABELS = {
    under_review: { label: 'Marked Under Review', icon: Eye, color: 'text-amber-600' },
    request_info: { label: 'Requested More Info', icon: Send, color: 'text-orange-600' },
    reject: { label: 'Rejected', icon: XCircle, color: 'text-red-600' },
    approve_convert: { label: 'Approved & Converted', icon: CheckCircle2, color: 'text-emerald-600' },
    assign_owner: { label: 'Owner Assigned', icon: User, color: 'text-blue-600' },
  };

  const formatTs = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-2">
      {audit.map(a => {
        const cfg = ACTION_LABELS[a.action_type] || { label: a.action_type, icon: History, color: 'text-slate-500' };
        const Icon = cfg.icon;
        return (
          <div key={a.audit_id} className="flex items-start gap-2 text-xs">
            <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${cfg.color}`} />
            <div>
              <p className="text-slate-700 font-medium">{cfg.label}</p>
              <p className="text-slate-400">{formatTs(a.timestamp)}</p>
              {a.details_json?.reason && <p className="text-slate-500 mt-0.5">Reason: {a.details_json.reason}</p>}
              {a.details_json?.owner_name && <p className="text-slate-500 mt-0.5">Assigned to: {a.details_json.owner_name}</p>}
              {a.details_json?.prospect_id && <p className="text-slate-500 mt-0.5">Prospect: #{a.details_json.prospect_id.substring(0, 8)}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ==================== MAIN PAGE ====================

export default function RFQDetailPage() {
  const { rfqId } = useParams();
  const navigate = useNavigate();
  const [rfq, setRfq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salesUsers, setSalesUsers] = useState([]);

  // Modal states
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showRequestInfoModal, setShowRequestInfoModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Form states
  const [rejectReason, setRejectReason] = useState('');
  const [rejectSendEmail, setRejectSendEmail] = useState(false);
  const [infoSiteId, setInfoSiteId] = useState('');
  const [infoItems, setInfoItems] = useState([]);
  const [infoCustomMsg, setInfoCustomMsg] = useState('');
  const [convertOwnerId, setConvertOwnerId] = useState('');
  const [convertNote, setConvertNote] = useState('');

  const fetchDetail = useCallback(async () => {
    try {
      const [rfqRes, usersRes] = await Promise.all([
        rfqAPI.getDetail(rfqId),
        rfqAPI.getSalesUsers()
      ]);
      setRfq(rfqRes.data);
      setSalesUsers(usersRes.data);
    } catch (e) {
      toast.error('Failed to load RFQ');
    } finally {
      setLoading(false);
    }
  }, [rfqId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  // ---- ACTIONS ----

  const handleUnderReview = async () => {
    setActionLoading(true);
    try {
      await rfqAPI.markUnderReview(rfqId);
      toast.success('RFQ marked as Under Review');
      fetchDetail();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestInfo = async () => {
    if (!infoSiteId || infoItems.length === 0) {
      toast.error('Select a site and at least one missing item');
      return;
    }
    setActionLoading(true);
    try {
      const res = await rfqAPI.requestMoreInfo(rfqId, {
        rfq_site_id: infoSiteId,
        missing_items: infoItems,
        custom_message: infoCustomMsg,
      });
      const emailInfo = res.data?.email_result?.mocked
        ? ' (Email mocked — Gmail API not yet configured)'
        : '';
      toast.success(`Info requested${emailInfo}`);
      setShowRequestInfoModal(false);
      setInfoItems([]);
      setInfoCustomMsg('');
      fetchDetail();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }
    setActionLoading(true);
    try {
      await rfqAPI.reject(rfqId, { reason: rejectReason, send_email: rejectSendEmail });
      toast.success('RFQ rejected');
      setShowRejectModal(false);
      setRejectReason('');
      fetchDetail();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConvert = async () => {
    if (!convertOwnerId) {
      toast.error('Select a sales owner');
      return;
    }
    setActionLoading(true);
    try {
      const res = await rfqAPI.approveConvert(rfqId, {
        owner_user_id: convertOwnerId,
        internal_note: convertNote,
      });
      toast.success(`Converted! Prospect created with ${res.data.site_mapping?.length} sites`);
      setShowConvertModal(false);
      fetchDetail();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Conversion failed');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleInfoItem = (key) => {
    setInfoItems(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Sidebar />
        <div className="lg:ml-64 p-6"><LoadingSpinner /></div>
      </div>
    );
  }

  if (!rfq) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Sidebar />
        <div className="lg:ml-64 p-6 text-center py-20">
          <p className="text-slate-500">RFQ not found</p>
          <Button variant="outline" onClick={() => navigate('/rfq')} className="mt-4">Back to RFQs</Button>
        </div>
      </div>
    );
  }

  const isTerminal = rfq.status === 'CONVERTED' || rfq.status === 'REJECTED';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="rfq-detail-page">
      <Sidebar />
      <div className="lg:ml-64">
        <div className="p-6 max-w-[1400px] mx-auto">
          {/* Breadcrumbs */}
          <Breadcrumbs
            items={[
              { label: 'Prospects', to: '/prospects' },
              { label: 'RFQs', to: '/prospects' },
              { label: rfq.company_name },
            ]}
            onBack={() => navigate('/prospects')}
          />

          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-xl font-bold text-slate-900" data-testid="rfq-detail-company">{rfq.company_name}</h1>
                <StatusBadge status={rfq.status} large />
              </div>
              <p className="text-xs text-slate-400 font-mono">RFQ #{rfq.rfq_id.substring(0, 8)}</p>
            </div>

            {/* Action buttons */}
            {!isTerminal && (
              <div className="flex flex-wrap gap-2" data-testid="rfq-action-buttons">
                {(rfq.status === 'NEW' || rfq.status === 'NEEDS_MORE_INFO') && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleUnderReview}
                    disabled={actionLoading}
                    data-testid="rfq-action-under-review"
                  >
                    <Eye className="w-3.5 h-3.5 mr-1" /> Mark Under Review
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setInfoSiteId(rfq.sites?.[0]?.rfq_site_id || ''); setShowRequestInfoModal(true); }}
                  disabled={actionLoading}
                  data-testid="rfq-action-request-info"
                >
                  <Send className="w-3.5 h-3.5 mr-1" /> Request Info
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300 hover:bg-red-50"
                  onClick={() => setShowRejectModal(true)}
                  disabled={actionLoading}
                  data-testid="rfq-action-reject"
                >
                  <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                </Button>
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => setShowConvertModal(true)}
                  disabled={actionLoading}
                  data-testid="rfq-action-convert"
                >
                  <ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Approve & Convert
                </Button>
              </div>
            )}
          </div>

          {/* Converted banner */}
          {rfq.status === 'CONVERTED' && rfq.converted_prospect && (
            <div
              className="mb-6 p-4 bg-violet-50 border border-violet-200 rounded-lg flex items-center justify-between"
              data-testid="rfq-converted-banner"
            >
              <div className="flex items-center gap-3">
                <ArrowRightLeft className="w-5 h-5 text-violet-600" />
                <div>
                  <p className="text-sm font-medium text-violet-800">Converted to Prospect</p>
                  <p className="text-xs text-violet-600">{rfq.converted_prospect.company_name} — #{rfq.converted_prospect.prospect_id.substring(0, 8)}</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/prospects/${rfq.converted_prospect.prospect_id}`)}
                className="border-violet-300 text-violet-700 hover:bg-violet-100"
                data-testid="rfq-view-prospect-btn"
              >
                <ExternalLink className="w-3.5 h-3.5 mr-1" /> View Prospect
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left column: main content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Contact Info Card */}
              <div className="bg-white rounded-lg border border-slate-200 p-5">
                <h3 className="text-sm font-semibold text-slate-800 mb-3">Contact Information</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider flex items-center gap-1"><User className="w-3 h-3" /> Contact</p>
                    <p className="text-sm text-slate-700 mt-0.5">{rfq.contact_name}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider flex items-center gap-1"><Mail className="w-3 h-3" /> Email</p>
                    <p className="text-sm text-slate-700 mt-0.5">{rfq.contact_email}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider flex items-center gap-1"><Phone className="w-3 h-3" /> Phone</p>
                    <p className="text-sm text-slate-700 mt-0.5">{rfq.contact_phone || '-'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider flex items-center gap-1"><MapPin className="w-3 h-3" /> City</p>
                    <p className="text-sm text-slate-700 mt-0.5">{rfq.city || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Sites */}
              <div>
                <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-slate-500" /> Sites ({rfq.sites?.length || 0})
                </h3>
                <div className="space-y-3">
                  {rfq.sites?.map((site, i) => <SiteCard key={site.rfq_site_id} site={site} index={i} />)}
                </div>
              </div>
            </div>

            {/* Right column: sidebar info */}
            <div className="space-y-5">
              {/* Completeness */}
              <div className="bg-white rounded-lg border border-slate-200 p-4">
                <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">Completeness</h4>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${rfq.completeness >= 80 ? 'bg-emerald-500' : rfq.completeness >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                      style={{ width: `${rfq.completeness || 0}%` }}
                    />
                  </div>
                  <span className="text-sm font-bold text-slate-700">{rfq.completeness || 0}%</span>
                </div>
              </div>

              {/* Savings Profile */}
              {rfq.savings_profile_json && (
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3 flex items-center gap-1">
                    <Leaf className="w-3.5 h-3.5 text-emerald-500" /> Savings Profile
                  </h4>
                  <div className="space-y-2">
                    {rfq.savings_profile_json.estimated_monthly_savings_inr && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Monthly Savings</span>
                        <span className="font-medium text-slate-700">
                          {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(rfq.savings_profile_json.estimated_monthly_savings_inr)}
                        </span>
                      </div>
                    )}
                    {rfq.savings_profile_json.diesel_displacement_liters && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Diesel Displaced</span>
                        <span className="font-medium text-slate-700">{rfq.savings_profile_json.diesel_displacement_liters} L/mo</span>
                      </div>
                    )}
                    {rfq.savings_profile_json.co2_reduction_kg && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">CO2 Reduced</span>
                        <span className="font-medium text-emerald-600">{rfq.savings_profile_json.co2_reduction_kg} kg/mo</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Owner */}
              <div className="bg-white rounded-lg border border-slate-200 p-4">
                <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Owner</h4>
                {rfq.owner_name ? (
                  <p className="text-sm text-slate-700">{rfq.owner_name}</p>
                ) : (
                  <p className="text-xs text-slate-400 italic">No owner assigned</p>
                )}
              </div>

              {/* Internal Notes */}
              {rfq.internal_notes && (
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Internal Notes</h4>
                  <p className="text-xs text-slate-600">{rfq.internal_notes}</p>
                </div>
              )}

              {/* Audit Trail */}
              <div className="bg-white rounded-lg border border-slate-200 p-4">
                <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3 flex items-center gap-1">
                  <History className="w-3.5 h-3.5" /> Audit Trail
                </h4>
                <AuditTimeline audit={rfq.audit_trail} />
              </div>

              {/* Rejection info */}
              {rfq.status === 'REJECTED' && rfq.rejection_reason && (
                <div className="bg-red-50 rounded-lg border border-red-200 p-4">
                  <h4 className="text-xs font-semibold text-red-700 uppercase tracking-wider mb-2">Rejection Reason</h4>
                  <p className="text-xs text-red-600">{rfq.rejection_reason}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ==================== MODALS ==================== */}

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="max-w-md" data-testid="rfq-reject-modal">
          <DialogHeader>
            <DialogTitle>Reject RFQ</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Reason (required)</Label>
              <Textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                placeholder="Explain why this RFQ is being rejected..."
                className="mt-1"
                data-testid="rfq-reject-reason-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={rejectSendEmail}
                onCheckedChange={setRejectSendEmail}
                id="reject-email"
                data-testid="rfq-reject-send-email"
              />
              <Label htmlFor="reject-email" className="text-sm">Send rejection email to customer</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectModal(false)}>Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleReject}
              disabled={actionLoading}
              data-testid="rfq-reject-confirm-btn"
            >
              {actionLoading ? 'Rejecting...' : 'Reject RFQ'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request More Info Modal */}
      <Dialog open={showRequestInfoModal} onOpenChange={setShowRequestInfoModal}>
        <DialogContent className="max-w-md" data-testid="rfq-request-info-modal">
          <DialogHeader>
            <DialogTitle>Request More Information</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Site</Label>
              <Select value={infoSiteId} onValueChange={setInfoSiteId}>
                <SelectTrigger className="mt-1" data-testid="rfq-info-site-select">
                  <SelectValue placeholder="Select site..." />
                </SelectTrigger>
                <SelectContent>
                  {rfq.sites?.map(s => (
                    <SelectItem key={s.rfq_site_id} value={s.rfq_site_id}>{s.site_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Missing Documents</Label>
              <div className="space-y-2 mt-2">
                {MISSING_ITEMS_OPTIONS.map(opt => (
                  <div key={opt.key} className="flex items-center gap-2">
                    <Checkbox
                      checked={infoItems.includes(opt.key)}
                      onCheckedChange={() => toggleInfoItem(opt.key)}
                      id={`info-${opt.key}`}
                      data-testid={`rfq-info-item-${opt.key}`}
                    />
                    <Label htmlFor={`info-${opt.key}`} className="text-sm">{opt.label}</Label>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label>Custom Message (optional)</Label>
              <Textarea
                value={infoCustomMsg}
                onChange={e => setInfoCustomMsg(e.target.value)}
                placeholder="Add any specific instructions..."
                className="mt-1"
                data-testid="rfq-info-custom-msg"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestInfoModal(false)}>Cancel</Button>
            <Button
              onClick={handleRequestInfo}
              disabled={actionLoading}
              data-testid="rfq-info-send-btn"
            >
              {actionLoading ? 'Sending...' : 'Send Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve & Convert Modal */}
      <Dialog open={showConvertModal} onOpenChange={setShowConvertModal}>
        <DialogContent className="max-w-md" data-testid="rfq-convert-modal">
          <DialogHeader>
            <DialogTitle>Approve & Convert to Prospect</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs text-emerald-700">
              <p className="font-medium">This will create:</p>
              <ul className="mt-1 space-y-0.5 ml-2">
                <li>- 1 Prospect record ({rfq.company_name})</li>
                <li>- {rfq.sites?.length || 0} Prospect Site(s)</li>
                <li>- Requirement drafts per site</li>
                <li>- Upload records migrated to Prospect Sites</li>
                <li>- Internal tasks (verify docs, run configurator, etc.)</li>
              </ul>
            </div>
            <div>
              <Label>Sales Owner (required)</Label>
              <Select value={convertOwnerId} onValueChange={setConvertOwnerId}>
                <SelectTrigger className="mt-1" data-testid="rfq-convert-owner-select">
                  <SelectValue placeholder="Select sales owner..." />
                </SelectTrigger>
                <SelectContent>
                  {salesUsers.map(u => (
                    <SelectItem key={u.user_id} value={u.user_id}>
                      {u.name} ({u.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Internal Note (optional)</Label>
              <Textarea
                value={convertNote}
                onChange={e => setConvertNote(e.target.value)}
                placeholder="Add notes about this conversion..."
                className="mt-1"
                data-testid="rfq-convert-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertModal(false)}>Cancel</Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={handleConvert}
              disabled={actionLoading}
              data-testid="rfq-convert-confirm-btn"
            >
              {actionLoading ? 'Converting...' : 'Approve & Convert'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
